import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { buildOcrInput, drawStroke } from './ink';
import ClassroomSchoolLetter from '../sound-hunt/ClassroomSchoolLetter';

const HandwritingPad = forwardRef(function HandwritingPad({ disabled = false, onChange, onInputType, exampleWord, celebrating = false, disabledMessage = '' }, ref) {
  const canvasRef = useRef(null);
  const strokes = useRef([]);
  const active = useRef(null);
  const frame = useRef(null);
  const change = useRef(onChange);
  change.current = onChange;

  function report() {
    change.current?.({ count: strokes.current.length, drawing: Boolean(active.current) });
  }
  function redraw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { width, height } = canvas.getBoundingClientRect();
    if (!width || !height) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    for (const stroke of strokes.current) drawStroke(ctx, stroke, width, height);
    if (active.current) drawStroke(ctx, active.current.stroke, width, height);
  }
  function scheduleDraw() {
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => { frame.current = null; redraw(); });
  }
  useEffect(() => {
    const observer = new ResizeObserver(redraw);
    observer.observe(canvasRef.current);
    redraw();
    return () => { observer.disconnect(); if (frame.current !== null) cancelAnimationFrame(frame.current); };
  }, []);

  useImperativeHandle(ref, () => ({
    undo() { if (!disabled && !active.current) { strokes.current.pop(); report(); redraw(); } },
    clear() { if (!disabled && !active.current) { strokes.current = []; report(); redraw(); } },
    // Parent-only lifecycle reset: a locked benchmark sample may be discarded
    // explicitly even while editing is disabled. Never interrupt active ink.
    reset() { if (!active.current) { strokes.current = []; report(); redraw(); } },
    createOcrInput() {
      if (active.current) return null;
      return buildOcrInput(strokes.current, canvasRef.current.getBoundingClientRect());
    },
  }));

  function point(event) {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)) };
  }
  function start(event) {
    if (disabled || active.current || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    onInputType?.(['mouse', 'touch', 'pen'].includes(event.pointerType) ? event.pointerType : 'unknown');
    active.current = { id: event.pointerId, stroke: { width: 0.014, points: [point(event)] } };
    event.currentTarget.setPointerCapture(event.pointerId);
    report(); scheduleDraw();
  }
  function move(event) {
    if (!active.current || event.pointerId !== active.current.id) return;
    event.preventDefault();
    const samples = event.nativeEvent.getCoalescedEvents?.();
    for (const sample of samples?.length ? samples : [event]) active.current.stroke.points.push(point(sample));
    scheduleDraw();
  }
  function finish(event) {
    if (!active.current || event.pointerId !== active.current.id) return;
    // Commit the visible partial stroke on cancel/lost capture; never invent an endpoint.
    if (event.type === 'pointerup') active.current.stroke.points.push(point(event));
    strokes.current.push(active.current.stroke); active.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    report(); scheduleDraw();
  }
  return <div className={`dyw-paper${celebrating ? ' dyw-paper-success' : ''}`}>
    <div className="dyw-guidelines" aria-hidden="true"><i className="dyw-line-top" /><i className="dyw-line-middle" /><i className="dyw-line-bottom" /></div>
    {exampleWord && <div className={`dyw-example${celebrating ? ' dyw-example-success' : ''}`} role="img" aria-label={`${exampleWord} ${celebrating ? 'güzel yazı örneği' : 'üzerinden yazma yardımı'}`}>
      <svg viewBox={`8 9 ${exampleWord.length * 100 - 16} 80`} preserveAspectRatio="xMidYMax meet" aria-hidden="true">
        {[...exampleWord].map((letter, index) => <svg key={index} x={index * 100} width="100" height="100" viewBox="0 0 100 100"><ClassroomSchoolLetter letter={letter} /></svg>)}
      </svg>
    </div>}
    <canvas ref={canvasRef} aria-label="Güzel yazı defteri; parmağın, kalemin veya farenle yaz" aria-describedby="dyw-pad-help"
      aria-disabled={disabled} onPointerDown={start} onPointerMove={move} onPointerUp={finish}
      onPointerCancel={finish} onLostPointerCapture={finish} />
    {disabled && disabledMessage && <div className="dyw-pad-locked-message" role="status" aria-live="polite">{disabledMessage}</div>}
  </div>;
});
export default HandwritingPad;
